import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Camera, UploadSimple } from "@phosphor-icons/react";
import { api, API, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { Button } from "@/components/ui/button";

export const ProviderAvatar = ({ name = "", src, size = 48, className = "" }) => {
  const initials =
    name
      .split(" ")
      .filter(Boolean)
      .slice(0, 2)
      .map((w) => w[0]?.toUpperCase())
      .join("") || "P";
  return (
    <span
      className={`avatar-circle ${className}`}
      style={{ height: size, width: size, fontSize: Math.round(size / 2.8) }}
      data-testid="provider-avatar"
    >
      {src ? <img src={src} alt={name} loading="lazy" /> : <span>{initials}</span>}
    </span>
  );
};

export const AvatarUpload = () => {
  const { t } = useLang();
  const { user, checkAuth } = useAuth();
  const fileRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const current = preview || (user?.has_avatar ? `${API}/providers/${user.id}/avatar?v=${user.avatar_version || ""}` : null);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreview(URL.createObjectURL(file));
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      await api.post("/profile/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await checkAuth?.();
      toast.success(t("pub_photo_saved"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="card-list flex flex-col gap-4 p-5 sm:flex-row sm:items-center sm:justify-between" data-testid="avatar-upload-card">
      <div className="flex items-center gap-4">
        <ProviderAvatar name={user?.business_name || user?.name || ""} src={current} size={64} />
        <div className="min-w-0">
          <p className="flex items-center gap-2 text-base text-ppc-text md:text-lg">
            <Camera size={18} color="#9184d9" /> {t("pub_photo_title")}
          </p>
          <p className="mt-1 max-w-lg text-[13px] leading-relaxed text-ppc-muted">{t("pub_photo_d")}</p>
        </div>
      </div>
      <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={onPick} className="hidden" data-testid="avatar-file-input" />
      <Button type="button" disabled={busy} onClick={() => fileRef.current?.click()} data-testid="avatar-upload-btn" className="btn-secondary shrink-0 gap-1.5 rounded-xl">
        <UploadSimple size={16} /> {t("ver_choose_file")}
      </Button>
    </div>
  );
};
